/**
 * Age resolution for scouting-target rows (SE-6930).
 * @module features/scouting/utils/resolveTargetAge
 */

import { differenceInYears } from "date-fns";

/**
 * Converts a date-ish value into a whole-year age, or null when it is unusable.
 * @remarks
 * `new Date("not a date")` yields an Invalid Date and `differenceInYears`
 * then returns `NaN` — which is NOT null, so the card's `age != null` guard
 * would let it through and render the literal string "NaNy". Every DOB that
 * reaches the row must therefore pass through this parse-and-validate step.
 * @param value - Raw DOB string from the API (may be absent or unparseable)
 * @returns Whole-year age, or null when the DOB is missing or invalid
 */
const ageFromDateOfBirth = (
  value: string | null | undefined
): number | null => {
  if (!value) {
    return null;
  }

  const dob = new Date(value);
  if (Number.isNaN(dob.getTime())) {
    return null;
  }

  const years = differenceInYears(new Date(), dob);
  return Number.isFinite(years) ? years : null;
};

/** The DOB-bearing subset of a player node consumed by {@link resolveTargetAge} */
export interface AgeSources {
  /** Transactional DOB — populated for manually-created players */
  readonly dateOfBirth?: string | null;
  /** Provider-pipeline bio record, which carries its own DOB and age */
  readonly bioData?: {
    /** Provider DOB — populated for provider-sourced players */
    readonly dateOfBirth?: string | null;
    /** Provider's precomputed age in years */
    readonly age?: number | null;
  } | null;
}

/**
 * Resolves the age shown on a scouting-target row (SE-6930).
 * @remarks
 * The row previously derived age from `player.dateOfBirth` alone, which the
 * schema documents as "Date of birth for manual players. Provider-sourced
 * players use bioData.dateOfBirth" — so every provider-sourced player (the
 * overwhelming majority of a watchlist) resolved to null and the mobile card's
 * age segment was blank for everyone.
 *
 * Precedence mirrors SE-7480's `selectClubName`: the transactional field
 * first, the provider pipeline last.
 *
 * 1. `player.dateOfBirth` — transactional, authoritative for manual players.
 * 2. `bioData.dateOfBirth` — provider pipeline DOB, exact for everyone else.
 * 3. `bioData.age` — provider's precomputed age; last resort for the rare
 *    player whose age the provider knows but whose DOB it does not expose.
 *    Floored, because it is a Float on the wire and the card renders "Ny".
 *
 * Reading a provider-pipeline field here is deliberate and is NOT the pattern
 * SE-7480 moved away from: that ticket's field was `currentClub`, which
 * changes on transfer and went stale behind the pipeline's uninvalidated 24h
 * cache (SE-7681). A date of birth never changes, so pipeline staleness cannot
 * make it wrong.
 * @param player - The target's player node
 * @returns Whole-year age, or null when no source knows it
 */
export const resolveTargetAge = (player: AgeSources): number | null => {
  const fromTransactional = ageFromDateOfBirth(player.dateOfBirth);
  if (fromTransactional !== null) {
    return fromTransactional;
  }

  const fromBioDob = ageFromDateOfBirth(player.bioData?.dateOfBirth);
  if (fromBioDob !== null) {
    return fromBioDob;
  }

  const bioAge = player.bioData?.age;
  return bioAge != null && Number.isFinite(bioAge) ? Math.floor(bioAge) : null;
};
